/* eslint-disable max-len */
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'store';
import {
  IChangeSelfPasswordBody,
  ILoginBody,
  IOtpAuthDto,
  ISignInResDto,
  TwoFAEnforcementDto,
} from 'store/user/types/api-bodies.types';
import { ActionCreator, AnyAction } from 'redux';
import { ActionTypes, IRegion } from 'store/user/types/state.types';
import { User } from 'store/user/types';
import { userActions } from 'store/user';
import profileService from 'services/api/customer/profile-service';
import { Service } from 'store/user/types/service';
import { authService } from 'services/api/auth';
import { fetchParamLimits } from 'store/virtual-machine/actions';
import { ServicePermissions } from 'components/Project/types/enums';
import { hasAnyServicePermission } from 'components/SideMenu/utils';
import { readTabRegion, writeTabRegion } from 'utils/tab-region';
import { clearSessionActivity } from 'utils/session-activity';

export const setToken: ActionCreator<AnyAction> = (auth: ISignInResDto) => ({
  type: ActionTypes.SET_TOKEN,
  payload: auth,
});

export const setUser: ActionCreator<AnyAction> = (user: User) => ({
  type: ActionTypes.SET_USER,
  payload: user,
});

export const setLoading: ActionCreator<AnyAction> = (value: boolean) => ({
  type: ActionTypes.SET_LOADING,
  payload: value,
});

export const setRegion: ActionCreator<AnyAction> = (region: IRegion) => ({
  type: ActionTypes.SET_REGION,
  payload: region,
});

export const selectTabRegion = (userId: number, region: IRegion) => (dispatch: ThunkUserDispatch) => {
  writeTabRegion(userId, region);
  dispatch(setRegion(region));
};

export const setRegions: ActionCreator<AnyAction> = (regions: IRegion[]) => ({
  type: ActionTypes.SET_REGIONS,
  payload: regions,
});

export const clearUser: ActionCreator<AnyAction> = () => ({
  type: ActionTypes.CLEAR,
});

export const setServices: ActionCreator<AnyAction> = (payload: Service[]) => ({
  type: ActionTypes.SET_SERVICES,
  payload,
});

export const setTwoFAEnforcement: ActionCreator<AnyAction> = (payload: TwoFAEnforcementDto[]) => ({
  type: ActionTypes.SET_TWO_FA_ENFORCEMENT,
  payload,
});
export const setLanguage: ActionCreator<AnyAction> = (payload: string) => ({
  type: ActionTypes.SET_LANG,
  payload,
});

export type ThunkUserDispatch = ThunkDispatch<RootState, void, AnyAction>;

export const fetchProfile = () => async (dispatch: ThunkUserDispatch, getState: () => RootState) => {
  dispatch(setLoading(true));
  const userData = (await profileService.getProfile()).data;
  const user = new User(userData.data);
  const { token, tokenExpiresAt } = getState().user;
  user.token = token;
  user.tokenExpiresAt = tokenExpiresAt;
  // Регион вкладки меняет только явный выбор в селекторе, ответ профиля его не переключает
  const tabRegion = readTabRegion(user.id);
  if (tabRegion) {
    user.region = tabRegion;
  } else if (user.region) {
    writeTabRegion(user.id, user.region);
  }

  const twoFa = (await profileService.get2FAEnforcement()).data.data;
  user.twoFaEnforcement = twoFa;

  if (twoFa.blocking) {
    dispatch(setUser(user));
    dispatch(setLoading(false));
    return;
  }

  const servicesRes = await profileService.getServices();
  user.services = servicesRes.data.data;

  dispatch(setUser(user));
  if (user.currentProject && hasAnyServicePermission(user.currentProject?.permissions, [
    ServicePermissions.VIRTUAL_SERVERS,
    ServicePermissions.NETWORK,
    ServicePermissions.BACKUPS,
    ServicePermissions.CUSTOM_IMAGES,
    ServicePermissions.DISKS,
    ServicePermissions.LOAD_BALANCERS,
    ServicePermissions.KUBERNETES,
    ServicePermissions.S3,
    ServicePermissions.MANAGED_DATABASES,
    ServicePermissions.ROUTERS,
    ServicePermissions.AUTO_SCALING_GROUPS,
  ])) {
    await dispatch(fetchParamLimits());
  }
  dispatch(setLoading(false));
};

// Регион вкладки фиксируем до запросов, иначе fetchProfile вернёт прежний регион из sessionStorage
export const changeRegion = (region: IRegion) => async (dispatch: ThunkUserDispatch, getState: () => RootState) => {
  dispatch(selectTabRegion(getState().user.id, region));
  // Серверное значение остаётся запасным для клиентов без X-Region-Id
  await profileService.setRegion(region.id);
  await dispatch(fetchProfile());
};

export const signIn = (body: ILoginBody, lang: string) => async (dispatch: ThunkUserDispatch): Promise<ISignInResDto> => {
  const { data } = await authService.signIn(body, lang);
  // Запись прошлой сессии убираем, иначе другая вкладка выйдет по ней и отзовёт новые токены.
  // Новую запись заведёт панель
  clearSessionActivity();
  dispatch(setToken(data));
  return data;
};

export const refreshToken = () => async (dispatch: ThunkUserDispatch): Promise<ISignInResDto> => {
  const { data } = await authService.refreshToken();
  dispatch(setToken(data));

  if (window.location.pathname !== 'settings/two-fa') {
    const twoFa = (await profileService.get2FAEnforcement()).data.data;
    dispatch(setTwoFAEnforcement(twoFa));
  }
  return data;
};

export const signInConfirm = (body: IOtpAuthDto, lang: string) => async (dispatch: ThunkUserDispatch): Promise<ISignInResDto> => {
  const { data } = await authService.signInOtpConfirm(body, lang);
  clearSessionActivity();
  dispatch(setToken(data));
  return data;
};

// eslint-disable-next-line max-len
export const changePassword = (body: IChangeSelfPasswordBody) => async (dispatch: ThunkUserDispatch): Promise<void> => {
  await profileService.changeSelfPassword(body);
  dispatch(setLoading(false));
};

export const signOut = async (dispatch: ThunkUserDispatch) => {
  await profileService.signOut();
  clearSessionActivity();
  dispatch(userActions.clearUser());
};

// Запись об активности остаётся: по ней страница входа покажет причину выхода
export const idleSignOut = async (dispatch: ThunkUserDispatch) => {
  await profileService.signOut().catch(() => null);
  dispatch(userActions.clearUser());
};

export const getRegions = async (dispatch: ThunkUserDispatch, getState: () => RootState) => {
  const { data } = await profileService.getUserRegions();
  const { regions } = data.data;
  dispatch(setRegions(regions));

  // Сохранённый во вкладке регион мог перестать быть доступным пользователю
  const { id: userId, region } = getState().user;
  if (regions.length && !regions.some((item) => item.id === region?.id)) {
    dispatch(selectTabRegion(userId, regions[0]));
  }
};

export const getServices = async (dispatch: ThunkUserDispatch) => {
  const { data } = await profileService.getServices();
  dispatch(setServices(data.data));
};

export const setApiLanguage = (value: string) => async (dispatch: ThunkUserDispatch) => {
  await profileService.setLanguage(value);
  dispatch(setLanguage(value));
};

export const checkTwoFAEnforcement = () => async (dispatch: ThunkUserDispatch) => {
  const twoFa = (await profileService.get2FAEnforcement()).data.data;
  dispatch(setTwoFAEnforcement(twoFa));
};
